import { useEffect, type ReactElement, type ReactNode } from 'react';
import { useT } from '../../../../app/di';
import { Button, IconButton, LinkButton } from '../../../../ui-kit/Button/Button';
import { Dialog } from '../../../../ui-kit/Dialog/Dialog';
import type { ViewerPosition } from '../model/post-media-viewer';
import styles from './PostMediaViewer.module.scss';

export interface PostMediaViewerProps {
  /** Имя файла — заголовок просмотра. */
  name: string;
  position: ViewerPosition;
  preview: ReactNode;
  /** Карточка медиа в медиатеке. */
  libraryHref: string;
  onLibrary(): void;
  onNavigate(mediaId: string): void;
  onClose(): void;
}

/** POST-UC-13: медиа поста крупно, листание по порядку поста (кнопки и ←/→), переход в медиатеку. */
export function PostMediaViewer({ name, position, preview, libraryHref, onLibrary, onNavigate, onClose }: PostMediaViewerProps): ReactElement {
  const { t } = useT();
  const { prev, next } = position;

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      // Стрелки в плеере и полях ввода — их собственные.
      if (event.target instanceof HTMLMediaElement || event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
      const target = event.key === 'ArrowLeft' ? prev : event.key === 'ArrowRight' ? next : undefined;
      if (target === undefined) return;
      event.preventDefault();
      onNavigate(target);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [prev, next, onNavigate]);

  return (
    <Dialog
      open
      wide
      title={name}
      closeLabel={t('common.close')}
      onClose={onClose}
      footer={
        <>
          <span className={styles.nav}>
            <IconButton icon="left" label={t('posts.viewer.prev')} disabled={prev === undefined} onClick={() => prev !== undefined && onNavigate(prev)} />
            <span className="u-small u-muted">{t('posts.viewer.position', { n: position.n, total: position.total })}</span>
            <IconButton icon="right" label={t('posts.viewer.next')} disabled={next === undefined} onClick={() => next !== undefined && onNavigate(next)} />
          </span>
          <LinkButton href={libraryHref} icon="image" onNavigate={onLibrary}>
            {t('posts.viewer.library')}
          </LinkButton>
          <Button variant="primary" onClick={onClose}>
            {t('common.close')}
          </Button>
        </>
      }
    >
      {preview}
    </Dialog>
  );
}
